/**
 * useStockItems - Rastreadores, chips e kits do Estoque
 *
 * 100% via RPCs: `get_stock_items`, `upsert_stock_tracker`, `upsert_stock_chip`,
 * `get_stock_free_components`, `form_stock_kit`, `dissolve_stock_kit`,
 * e `import_stock_items`. A situação do kit é controlada pela montagem/desmontagem.
 *
 * RELEASE: 2026-10-06 v1.1.0 - Campo `place` (local atual) nas linhas.
 * RELEASE: 2026-10-06 v1.0.0 - Criação inicial (Fase 3 do Estoque).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useTenantStockRealtime } from '@/hooks/realtime/useTenantStockRealtime';
export {
  CHIP_STATUSES,
  KIT_STATUSES,
  TRACKER_STATUSES,
  statusesFor,
  manualStatusesFor,
  stockStatusLabel,
} from '@/lib/stock-presentation';
import type { StockPresentationKind } from '@/lib/stock-presentation';

const logger = createLogger('useStockItems');

export type StockItemKind = StockPresentationKind;

/** Tom do selo de situação (verde = ok, âmbar = atenção, vermelho = problema). */
export const stockStatusTone = (v: string): 'emerald' | 'amber' | 'destructive' | 'muted' => {
  // 2026-10-10: Instalado (no veículo) também é situação saudável
  if (v === 'active' || v === 'installed') return 'emerald';
  if (['pending_receipt', 'maintenance', 'homologation', 'paused'].includes(v)) return 'amber';
  if (['damaged', 'lost', 'cancelled'].includes(v)) return 'destructive';
  return 'muted';
};

/** Linha devolvida pela listagem (campos variam por tipo). */
export interface StockItemRow {
  id: string;
  status: string;
  notes: string | null;
  is_active: boolean;
  // rastreador
  imei?: string;
  serial_number?: string | null; // legado: não exibido desde 2026-10-08
  esn?: string | null;
  supplier_person_id?: string | null;
  supplier_name?: string | null;
  tracker_model_id?: string | null;
  model_name?: string | null;
  manufacturer_name?: string | null;
  // chip
  iccid?: string;
  line_number?: string | null;
  imsi?: string | null;
  carrier_name?: string | null;
  /** v1.2.0 (2026-10-07): plano de dados vinculado e datas somente leitura */
  data_plan_id?: string | null;
  data_plan_name?: string | null;
  data_plan_carriers?: string[] | null;
  /** v1.3.0 (2026-10-08) */
  data_plan_package_mb?: number | null;
  data_plan_supplier_name?: string | null;
  activated_at?: string | null;
  cancelled_at?: string | null;
  // vínculo com kit
  kit_id?: string | null;
  kit_code?: string | null;
  // kit
  code?: string;
  tracker_id?: string;
  chip_id?: string;
  formed_at?: string;
  dissolved_at?: string | null;
  dissolve_reason?: string | null;
  /** get_stock_items v1.8.0: situação de cada componente do kit */
  tracker_status?: string | null;
  chip_status?: string | null;
  /** v1.1.0 (Fase 5): local atual (última movimentação) */
  place?: import('@/hooks/useStockMovements').StockPlace | null;
}

export interface StockItemsFilters {
  search: string;
  status: string;
  kit: 'all' | 'in_kit' | 'free';
  active: 'active' | 'inactive' | 'all';
  page: number;
  pageSize: number;
}

export interface ImportReport {
  total: number;
  imported: number;
  errors: { line: number; value: string | null; error: string }[];
}

// Mantém o `this` do cliente ao chamar RPCs fora da tipagem gerada
const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, name, args);

/** Lista paginada por tipo de item. */
export function useStockItemsList(kind: StockItemKind, filters: StockItemsFilters) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  // Tempo real: movimentações/homologações do tenant recarregam listagem e KPIs (canal único)
  useTenantStockRealtime([['stock-items'], ['stock-free'], ['stock-overview']], !!tenantId);
  return useQuery({
    queryKey: ['stock-items', kind, tenantId, filters],
    enabled: !!tenantId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await rpc('get_stock_items', {
        p_kind: kind,
        p_search: filters.search || null,
        p_status: filters.status === 'all' ? null : filters.status,
        p_kit_filter: kind === 'kit' ? 'all' : filters.kit,
        p_active: filters.active,
        p_page: filters.page,
        p_page_size: filters.pageSize,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar itens do estoque', { kind, error: error.message });
        throw new Error(error.message);
      }
      const p = (data || {}) as { total?: number; items?: StockItemRow[] };
      return { total: p.total ?? 0, items: p.items ?? [] };
    },
  });
}

/** Rastreadores ou chips livres (fora de kit) para montar um kit. */
export function useStockFreeComponents(kind: 'tracker' | 'chip', search: string, enabled: boolean) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['stock-free', kind, tenantId, search],
    enabled: !!tenantId && enabled,
    queryFn: async () => {
      const { data, error } = await rpc('get_stock_free_components', { p_kind: kind, p_search: search || null, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      // status/plano exibidos no seletor de montagem de kit (v1.1.0 da RPC)
      return (data as { id: string; label: string; extra: string | null; status: string; data_plan_name?: string | null }[]) ?? [];
    },
  });
}

/** Mutações de itens e kits. */
export function useStockItemMutations() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['stock-items'] });
    qc.invalidateQueries({ queryKey: ['stock-free'] });
    qc.invalidateQueries({ queryKey: ['stock-movements'] });
    // 2026-10-16: pesquisa de itens da remessa (Logística) não pode exibir lista antiga
    qc.invalidateQueries({ queryKey: ['shipment-candidates'] });
  };

  const upsertItem = useMutation({
    mutationFn: async ({ kind, id, data }: { kind: 'tracker' | 'chip'; id: string | null; data: Record<string, unknown> }) => {
      const fn = kind === 'tracker' ? 'upsert_stock_tracker' : 'upsert_stock_chip';
      const idKey = kind === 'tracker' ? 'p_tracker_id' : 'p_chip_id';
      const { error } = await rpc(fn, { p_data: data, [idKey]: id, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Item salvo');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const formKit = useMutation({
    mutationFn: async ({ trackerId, chipId, notes }: { trackerId: string; chipId: string; notes: string }) => {
      const { data, error } = await rpc('form_stock_kit', { p_tracker_id: trackerId, p_chip_id: chipId, p_notes: notes || null, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return data as { id: string; code: string };
    },
    onSuccess: (d) => {
      invalidate();
      toast.success(`Kit ${d.code} montado`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dissolveKit = useMutation({
    mutationFn: async ({ kitId, reason }: { kitId: string; reason: string }) => {
      const { error } = await rpc('dissolve_stock_kit', { p_kit_id: kitId, p_reason: reason || null, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Kit desmontado');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const importItems = useMutation({
    mutationFn: async ({ kind, rows, defaults }: { kind: 'tracker' | 'chip'; rows: Record<string, string>[]; defaults: Record<string, unknown> }) => {
      const { data, error } = await rpc('import_stock_items', { p_kind: kind, p_rows: rows, p_defaults: defaults, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return data as ImportReport;
    },
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message),
  });

  return { upsertItem, formKit, dissolveKit, importItems };
}
